import React, { useState } from 'react';
import { X, ChevronRight, Users, ClipboardList, Mail, FileCheck, Receipt, Settings, CheckCircle2, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { useVersion } from '../../hooks/useVersion';
import { parentStudentStatus } from '~/pages/ParentEnrollment/parentStudentStatus';

interface SectionConfig {
  id: string;
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  content: React.ComponentType;
}

const SECTIONS: SectionConfig[] = [
  {
    id: 'overview',
    title: 'System Overview',
    icon: Sparkles,
    content: OverviewSection,
  },
  {
    id: 'families',
    title: 'Managing Families',
    icon: Users,
    content: FamiliesSection,
  },
  {
    id: 'enrollment-workflow',
    title: 'Enrollment Workflow',
    icon: ClipboardList,
    content: EnrollmentWorkflowSection,
  },
  {
    id: 'invites',
    title: 'Sending Invites',
    icon: Mail,
    content: InvitesSection,
  },
  {
    id: 'packets',
    title: 'Enrollment Packets',
    icon: FileCheck,
    content: PacketsSection,
  },
  {
    id: 'invoices',
    title: 'Invoices & Payments',
    icon: Receipt,
    content: InvoicesSection,
  },
  {
    id: 'settings',
    title: 'School Settings',
    icon: Settings,
    content: SchoolSettingsSection,
  },
];

function OverviewSection() {
  return (
    <div className="space-y-6">
      <p className="text-lg text-muted-foreground leading-relaxed">
        Welcome to your school management system! This guide will help you understand how everything works together to manage your school's enrollment process.
      </p>

      <Card className="border-2 border-primary/30 bg-gradient-to-r from-primary/5 to-primary/10">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-primary" />
            School Year Centric Design
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <p>
            Most of the system is organized around the <strong>selected school year</strong>. Use the school year selector in the sidebar to switch between years.
          </p>
          <p>
            When you select a school year, the pages you view—enrollments, invoices, student lists—are all scoped to that year. This keeps everything organized and lets you easily compare across years.
          </p>
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-2">
        <Card className="border-primary/20 bg-primary/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="h-4 w-4 text-primary" />
              Families First
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            Everything starts with families. Add families to your system, then enroll them for each school year.
          </CardContent>
        </Card>

        <Card className="border-primary/20 bg-primary/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <ClipboardList className="h-4 w-4 text-primary" />
              School Years
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            Each school year has its own enrollment cycle. Returning families re-enroll, new families apply.
          </CardContent>
        </Card>

        <Card className="border-primary/20 bg-primary/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Mail className="h-4 w-4 text-primary" />
              Invite System
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            Send enrollment invites to families. They receive a link to confirm their information and complete enrollment.
          </CardContent>
        </Card>

        <Card className="border-primary/20 bg-primary/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <FileCheck className="h-4 w-4 text-primary" />
              Digital Packets
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            Enrollment packets with tuition agreements and policies are signed digitally—no paper needed.
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function FamiliesSection() {
  return (
    <div className="space-y-6">
      <p className="text-muted-foreground">
        The Families page is your central hub for managing all families in your school.
      </p>

      <div className="space-y-4">
        <div className="flex gap-4 items-start">
          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
            <span className="text-sm font-medium text-primary">1</span>
          </div>
          <div>
            <h4 className="font-medium">Add New Families</h4>
            <p className="text-sm text-muted-foreground">
              Click "Add Family" to create a new family record. Enter the family name, then add at least one parent (mom or dad) with their contact information.
              Mark those who can make decisions on behalf of the family as <strong>Primary</strong>. Ensure the primary contact has an email address so they can later be set up as a user in the system.
            </p>
          </div>
        </div>

        <div className="flex gap-4 items-start">
          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
            <span className="text-sm font-medium text-primary">2</span>
          </div>
          <div>
            <h4 className="font-medium">Add Children</h4>
            <p className="text-sm text-muted-foreground">
              Open a family's detail page and add children. Enter their core identity information: name, date of birth, and gender.
              You can also add any profile data you have available.
            </p>
            <p className="text-sm text-muted-foreground mt-2 italic">
              Note: Once the system is fully set up, this data is normally collected during interest forms, applications, or re-enrollment confirmations.
            </p>
          </div>
        </div>

        <div className="flex gap-4 items-start">
          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
            <span className="text-sm font-medium text-primary">3</span>
          </div>
          <div>
            <h4 className="font-medium">View Enrollment Status</h4>
            <p className="text-sm text-muted-foreground">
              The Enrollment tab on each family shows their current enrollment status for the selected school year.
            </p>
          </div>
        </div>
      </div>

      <Card className="bg-amber-50 border-amber-200 dark:bg-amber-950/20 dark:border-amber-900">
        <CardContent className="pt-4">
          <p className="text-sm">
            <strong>Tip:</strong> You can import families in bulk if you're migrating from another system. Contact support for assistance with data imports.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function EnrollmentWorkflowSection() {
  const workflowSteps = [
    {
      family: { state: 'DRAFT', label: 'Draft', description: 'Initial setup. Inquiring families may start here. Enrollment marked as NEW_ADMISSION or RE_ENROLLMENT.' },
      student: null,
    },
    {
      family: null,
      student: {
        state: 'PENDING_FAMILY_CONFIRMATION',
        label: parentStudentStatus('PENDING_FAMILY_CONFIRMATION'),
        description: "Student added; the family hasn't said yet whether the child is coming.",
      },
    },
    {
      family: { state: 'READY_TO_INVITE', label: 'Ready to Invite', description: 'Queued; an admin sends the invite (Send invite) once the enrollment window is open.' },
      student: null,
    },
    {
      family: { state: 'INVITED', label: 'Invited', description: 'Invite sent, waiting for family response.' },
      student: null,
    },
    {
      family: { state: 'IN_PROGRESS', label: 'In Progress', description: 'Family is completing their confirmation or application form.' },
      student: null,
    },
    {
      family: { state: 'NEEDS_REVIEW', label: 'Needs Review', description: 'Submitted, awaiting admissions review.' },
      student: { state: 'PENDING_ADMISSIONS_REVIEW', label: 'Pending Review', description: 'Student info submitted for review.' },
    },
    {
      family: null,
      student: { state: 'WAITLISTED / ACCEPTED / DENIED', label: 'Decision Made', description: 'Student may be waitlisted, accepted pending deposit, or denied.' },
    },
    {
      family: { state: 'APPROVED', label: 'Approved', description: 'Family enrollment approved by admissions.' },
      student: null,
    },
    {
      family: { state: 'PACKET_SENT', label: 'Packet Sent', description: 'Enrollment packet sent for signing.' },
      student: null,
    },
    {
      family: { state: 'PACKET_SUBMITTED', label: 'Packet Submitted', description: 'Family signs tuition agreement and policies.' },
      student: { state: 'ENROLLED', label: 'Enrolled', description: 'Student is officially enrolled.' },
    },
    {
      family: { state: 'PACKET_COMPLETED', label: 'Complete', description: 'Curriculum fee payment processed. Enrollment complete!' },
      student: null,
      isComplete: true,
    },
  ];

  return (
    <div className="space-y-6">
      <p className="text-muted-foreground">
        Enrollments track both <strong>family</strong> and <strong>student</strong> progress through the enrollment process. Here's how they work together:
      </p>

      <div className="flex gap-6 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-primary" />
          <span className="text-muted-foreground">Family Enrollment</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-violet-500" />
          <span className="text-muted-foreground">Student Enrollment</span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        <div className="font-medium text-sm text-primary border-b pb-2">Family Enrollment Flow</div>
        <div className="font-medium text-sm text-violet-600 border-b pb-2">Student Enrollment Flow</div>
      </div>

      <div className="relative">
        <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-primary/20" />
        <div className="absolute left-[calc(50%+1rem)] top-0 bottom-0 w-0.5 bg-violet-200" />

        <div className="space-y-3">
          {workflowSteps.map((step, idx) => (
            <div key={idx} className="grid grid-cols-2 gap-6 min-h-[4rem]">
              <div className="flex gap-3 items-start relative">
                {step.family ? (
                  <>
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 z-10 ${
                      step.isComplete
                        ? 'bg-green-500 text-white'
                        : 'bg-background border-2 border-primary'
                    }`}>
                      {step.isComplete ? (
                        <CheckCircle2 className="h-3.5 w-3.5" />
                      ) : (
                        <span className="text-xs font-medium">{workflowSteps.filter((s, i) => i <= idx && s.family).length}</span>
                      )}
                    </div>
                    <div className="pb-2 flex-1">
                      <Badge variant="outline" className="font-mono text-xs mb-1">
                        {step.family.state}
                      </Badge>
                      <p className="text-xs text-muted-foreground">
                        {step.family.description}
                      </p>
                    </div>
                  </>
                ) : (
                  <div className="w-7 shrink-0" />
                )}
              </div>

              <div className="flex gap-3 items-start relative">
                {step.student ? (
                  <>
                    <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 z-10 bg-background border-2 border-violet-500">
                      <span className="text-xs font-medium text-violet-600">
                        {workflowSteps.filter((s, i) => i <= idx && s.student).length}
                      </span>
                    </div>
                    <div className="pb-2 flex-1">
                      <Badge variant="outline" className="font-mono text-xs mb-1 border-violet-300 text-violet-700">
                        {step.student.state}
                      </Badge>
                      <p className="text-xs text-muted-foreground">
                        {step.student.description}
                      </p>
                    </div>
                  </>
                ) : (
                  <div className="w-7 shrink-0" />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <Card className="bg-blue-50 border-blue-200 dark:bg-blue-950/20 dark:border-blue-900">
        <CardContent className="pt-4">
          <p className="text-sm">
            <strong>Note:</strong> A family enrollment can have multiple student enrollments. Each student's admission decision is tracked separately (waitlist, accept, deny).
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function InvitesSection() {
  return (
    <div className="space-y-6">
      <p className="text-muted-foreground">
        The invite system lets you notify families that it's time to enroll for the upcoming year.
      </p>

      <div className="space-y-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">For Returning Families (Re-enrollment)</CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-2">
            <p>1. Go to <strong>Enrollments</strong> and click <strong>Settings</strong></p>
            <p>2. Enable <strong>"Re-enrollment Open"</strong> for the school year</p>
            <p>3. Returning families will appear with state "Ready to Invite"</p>
            <p>4. Select families and click <strong>"Send Invites"</strong></p>
            <p>5. Families receive an email with a link to confirm their enrollment</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">For New Families (Open Enrollment)</CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-2">
            <p>1. Add the new family in the <strong>Families</strong> section</p>
            <p>2. Go to their detail page and click <strong>Start Enrollment</strong></p>
            <p>3. Enable <strong>"Open Enrollment"</strong> in Settings when ready</p>
            <p>4. Queue and send invites just like returning families</p>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-blue-50 border-blue-200 dark:bg-blue-950/20 dark:border-blue-900">
        <CardContent className="pt-4">
          <p className="text-sm">
            <strong>Note:</strong> Families access their enrollment through a secure link sent to their email. They don't need to create an account—the link contains a unique token.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function PacketsSection() {
  return (
    <div className="space-y-6">
      <p className="text-muted-foreground">
        After you approve a family's enrollment, you send them an Enrollment Packet to finalize everything.
      </p>

      <div className="space-y-4">
        <div className="flex gap-4 items-start">
          <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center shrink-0">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
          </div>
          <div>
            <h4 className="font-medium">What's in a Packet?</h4>
            <ul className="text-sm text-muted-foreground mt-1 space-y-1 list-disc list-inside">
              <li>Tuition agreement with payment options</li>
              <li>School policies acknowledgment</li>
              <li>Media release consent</li>
              <li>Emergency medical authorization</li>
              <li>Digital signature</li>
            </ul>
          </div>
        </div>

        <div className="flex gap-4 items-start">
          <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center shrink-0">
            <Receipt className="h-4 w-4 text-blue-600" />
          </div>
          <div>
            <h4 className="font-medium">Invoice Timing</h4>
            <div className="text-sm text-muted-foreground mt-1 space-y-2">
              <p>
                <strong>Draft invoices</strong> may be created when a family is invited to re-enroll or apply. These remain editable until the packet is sent for signing.
              </p>
              <p>
                Once the family <strong>signs the packet</strong>, invoices transition from DRAFT to UNPAID (posted) and become visible to families.
              </p>
            </div>
          </div>
        </div>

        <div className="flex gap-4 items-start">
          <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center shrink-0">
            <FileCheck className="h-4 w-4 text-purple-600" />
          </div>
          <div>
            <h4 className="font-medium">After They Sign</h4>
            <p className="text-sm text-muted-foreground mt-1">
              Once the family signs their packet, students are officially enrolled. The enrollment moves to PACKET_COMPLETED once the enrollment fee payment is processed.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function InvoicesSection() {
  return (
    <div className="space-y-6">
      <p className="text-muted-foreground">
        Track tuition and fee payments for each family. Invoices move through several states during their lifecycle.
      </p>

      <div className="space-y-3">
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <Badge variant="secondary">DRAFT</Badge>
              <CardTitle className="text-sm">Draft Invoices</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            Created automatically when enrollment begins. Not visible to families and fully editable until the packet is signed.
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <Badge variant="destructive">UNPAID</Badge>
              <CardTitle className="text-sm">Posted / Unpaid</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            Posted and visible to families in their portal. Awaiting payment.
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <Badge className="bg-amber-500 text-white">PROCESSING</Badge>
              <CardTitle className="text-sm">Payment Processing</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            Payment has been submitted but is still clearing. Shows as "Paid" to families with a processing indicator. Payment may take 1-3 business days to post.
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <Badge className="bg-green-500 text-white">PAID</Badge>
              <CardTitle className="text-sm">Paid / Complete</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            Payment has cleared and fully posted. Invoice is complete.
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <Badge variant="outline">VOID</Badge>
              <CardTitle className="text-sm">Voided</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            Cancelled invoices that shouldn't be paid. Use for corrections or when a family withdraws.
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function SchoolSettingsSection() {
  return (
    <div className="space-y-6">
      <p className="text-muted-foreground">
        Configure your school's settings to customize the enrollment experience.
      </p>

      <div className="space-y-4">
        <div className="flex gap-4 items-start">
          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
            <Receipt className="h-4 w-4 text-primary" />
          </div>
          <div>
            <h4 className="font-medium">Tuition & Fees</h4>
            <p className="text-sm text-muted-foreground">
              Configure tuition rates, payment plans, and fee structures. Set up different rates for different grade levels or enrollment types.
            </p>
          </div>
        </div>

        <div className="flex gap-4 items-start">
          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
            <ClipboardList className="h-4 w-4 text-primary" />
          </div>
          <div>
            <h4 className="font-medium">Enrollment Dates</h4>
            <p className="text-sm text-muted-foreground">
              Set when re-enrollment opens for returning families and when open enrollment begins for new admissions. Control the enrollment timeline for each school year.
            </p>
          </div>
        </div>

        <div className="flex gap-4 items-start">
          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
            <FileCheck className="h-4 w-4 text-primary" />
          </div>
          <div>
            <h4 className="font-medium">Forms & Packets</h4>
            <p className="text-sm text-muted-foreground">
              Customize enrollment forms, application questions, and packet templates. Configure which policies and agreements families must acknowledge.
            </p>
          </div>
        </div>

        <div className="flex gap-4 items-start">
          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
            <Settings className="h-4 w-4 text-primary" />
          </div>
          <div>
            <h4 className="font-medium">School Year Management</h4>
            <p className="text-sm text-muted-foreground">
              Create and manage school years. Set administrative start/end dates and configure year-specific settings.
            </p>
          </div>
        </div>
      </div>

      <Card className="bg-amber-50 border-amber-200 dark:bg-amber-950/20 dark:border-amber-900">
        <CardContent className="pt-4">
          <p className="text-sm">
            <strong>Tip:</strong> Most settings are configured per school year. Make sure you have the correct school year selected before making changes.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

interface HelpOverlayProps {
  isOpen: boolean;
  onClose: () => void;
}

function HelpOverlay({ isOpen, onClose }: HelpOverlayProps) {
  const [activeSection, setActiveSection] = useState('overview');
  const { shortVersion, gitSha } = useVersion();

  if (!isOpen) return null;

  const ActiveContent = SECTIONS.find(s => s.id === activeSection)?.content || OverviewSection;

  return (
    <div className="fixed inset-0 z-50 bg-background/95 backdrop-blur-sm overflow-hidden">
      <header className="sticky top-0 z-10 bg-background/80 backdrop-blur border-b">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Help & Getting Started</h1>
            <p className="text-sm text-muted-foreground">Learn how to manage your school's enrollment</p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-8 flex gap-8 h-[calc(100vh-80px)] overflow-hidden">
        <nav className="w-64 shrink-0">
          <div className="space-y-1">
            {SECTIONS.map((section) => {
              const Icon = section.icon;
              const isActive = activeSection === section.id;
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
                    isActive
                      ? 'bg-primary text-primary-foreground'
                      : 'hover:bg-muted text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span className="text-sm font-medium">{section.title}</span>
                  {isActive && <ChevronRight className="h-4 w-4 ml-auto" />}
                </button>
              );
            })}
          </div>

          <div className="mt-8 p-4 rounded-lg bg-muted/50 border">
            <p className="text-sm text-muted-foreground">
              Need more help? Contact support at{' '}
              <a href="mailto:support@solastack.com" className="text-primary hover:underline">
                support@solastack.com
              </a>
            </p>
            <p className="text-xs text-muted-foreground/60 mt-2">
              {shortVersion} · Build {gitSha}
            </p>
          </div>
        </nav>

        <main className="flex-1 overflow-y-auto pr-4">
          <div className="mb-6">
            <h2 className="text-xl font-semibold">
              {SECTIONS.find(s => s.id === activeSection)?.title}
            </h2>
          </div>
          <ActiveContent />
        </main>
      </div>
    </div>
  );
}

export default HelpOverlay;
